import {
  validateCanonicalRenderIndices,
  validateMeshCoherence,
  validateTopology,
} from "./ops/validateTopology.ts";
import type { SceneObject } from "./types.ts";
import {
  isEmptyObject,
  isImagePlaneObject,
  isLightObject,
  isMannequinObject,
} from "./types.ts";

export interface SceneObjectValidationIssue {
  objectId: string;
  objectName: string;
  reason: string;
}

function issue(obj: SceneObject, reason: string): SceneObjectValidationIssue {
  return { objectId: obj.id, objectName: obj.name, reason };
}

function hasRenderableGeometry(obj: SceneObject): boolean {
  return (
    !isLightObject(obj) &&
    !isEmptyObject(obj) &&
    !isMannequinObject(obj) &&
    !isImagePlaneObject(obj)
  );
}

export function validateSceneObject(
  obj: SceneObject,
): SceneObjectValidationIssue | null {
  if (!hasRenderableGeometry(obj)) return null;

  if (obj.vertices.length % 3 !== 0) {
    return issue(
      obj,
      `vertex buffer length ${obj.vertices.length} is not divisible by 3`,
    );
  }

  const vertexCount = obj.vertices.length / 3;

  if (obj.baseVertices && obj.baseVertices.length !== obj.vertices.length) {
    return issue(
      obj,
      `baseVertices length ${obj.baseVertices.length} does not match vertices length ${obj.vertices.length}`,
    );
  }
  if (obj.skinRadii && obj.skinRadii.length !== vertexCount) {
    return issue(
      obj,
      `skinRadii length ${obj.skinRadii.length} does not match vertexCount ${vertexCount}`,
    );
  }

  try {
    validateTopology(obj.topology, vertexCount, obj.name);
    validateMeshCoherence(
      obj.vertices,
      obj.topology,
      obj.loopNormals,
      obj.uvs,
      obj.name,
    );
    validateCanonicalRenderIndices(obj.topology, obj.indices, obj.name);
  } catch (error) {
    if (error instanceof Error) {
      return issue(obj, error.message);
    }
    return issue(obj, String(error));
  }

  return null;
}
